// memorybox_web entry: wires config, store, world, camera, input,
// and compose together.

import { BOARD_CONFIG } from "./config.ts";
import { uuid } from "@ts/core/uuid.ts";
import type { Board, BoardConfig, Mine, Note } from "@ts/core/types.ts";
import { STICKERS, configFromBoard, stickerArt } from "@ts/core/types.ts";
import type { Camera } from "@ts/board/camera.ts";
import { cameraOver, screenToWorld } from "@ts/board/camera.ts";
import { spiralPosition } from "@ts/board/placement.ts";
import { collides, resolve, type Rect } from "@ts/board/collide.ts";
import { NO_MEDIA, type Store } from "@ts/store/store.ts";
import { LocalStore } from "@browser/store/local.ts";
import { SupabaseStore } from "@browser/store/supabase.ts";
import {
  applyCamera,
  buildNoteBody,
  initWorld,
  mastheadRect,
  moveNote,
  noteElOf,
  noteSize,
  removeNote,
  renderMasthead,
  setGhost,
  setMastheadIntro,
  upsertNote,
} from "@browser/board/world.ts";
import { attachInput } from "@browser/board/input.ts";
import { initCompose } from "@browser/compose/compose.ts";
import { $ } from "@browser/util/dom.ts";

// Which box is this? Root URL = the legacy Old Friend board (config.ts);
// /b/<slug> = a board created in the studio, loaded from the database.
function parseSlug(): string | null {
  const m = location.pathname.match(/\/b\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
}

let cfg: BoardConfig = BOARD_CONFIG;
let boardId: string | null = null; // null = legacy board
let store!: Store;

// Per-board localStorage so your word/ownership on one box never leaks
// to another. The legacy board keeps its original (un-suffixed) keys so
// live guests don't lose what they've already saved.
const nsKey = (base: string) => (boardId ? `${base}:${boardId}` : base);

const normalizeWord = (w: string) => w.replace(/\s+/g, "").toLowerCase();
const getRoomWord = () => {
  const w = localStorage.getItem(nsKey("memorybox-word"));
  return w ? normalizeWord(w) : null;
};

// Anonymous per-browser id for self-owned product analytics (global).
const visitorId = (() => {
  let v = localStorage.getItem("memorybox-visitor");
  if (!v) {
    v = uuid();
    localStorage.setItem("memorybox-visitor", v);
  }
  return v;
})();

const track = (name: string, props?: Record<string, unknown>) =>
  store.track(visitorId, name, props);

const restHeaders = {
  apikey: BOARD_CONFIG.supabaseAnonKey,
  Authorization: `Bearer ${BOARD_CONFIG.supabaseAnonKey}`,
};

async function fetchBoard(slug: string): Promise<Board | null> {
  try {
    const res = await fetch(
      `${BOARD_CONFIG.supabaseUrl}/rest/v1/boards?slug=eq.${encodeURIComponent(slug)}&select=*&limit=1`,
      { headers: restHeaders }
    );
    const rows = await res.json();
    return rows?.[0] ?? null;
  } catch {
    return null;
  }
}

// Fetch the notes with a plain REST call — no Supabase library needed —
// so they can paint the moment the page loads, long before the client
// library finishes downloading.
async function fetchNotesFast(): Promise<Note[] | null> {
  const base = BOARD_CONFIG.supabaseUrl;
  const media = (p?: string | null) =>
    p ? `${base}/storage/v1/object/public/board-media/${p}` : null;
  const q = (filter: string) =>
    fetch(`${base}/rest/v1/notes?select=*&order=created_at.asc${filter}`, { headers: restHeaders });
  try {
    let res = await q(boardId ? `&board_id=eq.${boardId}` : `&board_id=is.null`);
    if (!res.ok && !boardId) res = await q(""); // pre-migration: no board_id column
    if (!res.ok) return null;
    const rows = await res.json();
    return rows.map((n: any) => ({
      ...n,
      photo_url: media(n.photo_path),
      audio_url: media(n.audio_path),
    }));
  } catch {
    return null;
  }
}

/** In-page room word card (window.prompt is broken in WhatsApp /
 *  Instagram in-app browsers). Saving the word reloads to reconnect
 *  with it attached, then resumes what the person was doing -
 *  including any half-typed note. */
function askRoomWord(then: () => void, wrongWord = false) {
  $("word-error").hidden = !wrongWord;
  $("word-overlay").hidden = false;
  const input = $<HTMLInputElement>("word-input");
  input.value = "";
  setTimeout(() => input.focus(), 50);
  const submit = () => {
    const w = normalizeWord(input.value);
    if (!w) return;
    localStorage.setItem("memorybox-word", w);
    store.setRoomWord?.(w);
    $("word-overlay").hidden = true;
    then();
  };
  $("word-submit").onclick = submit;
  input.onkeydown = (e) => {
    if (e.key === "Enter") submit();
  };
}

/** Run `action` once we hold a room word (asking for it if needed). */
function withRoomWord(action: () => void) {
  if (!store.remote || getRoomWord()) action();
  else askRoomWord(action);
}

let freezeAt = new Date(cfg.freezeAt);
const frozen = () => Date.now() >= freezeAt.getTime();

const getMine = (): Mine[] => JSON.parse(localStorage.getItem(nsKey("memorybox-mine")) || "[]");
const setMine = (m: Mine[]) => localStorage.setItem(nsKey("memorybox-mine"), JSON.stringify(m));
const mineOf = (id: string): Mine | undefined => getMine().find((m) => m.id === id);

const notes = new Map<string, Note>();

// ── camera ──
const stage = $("stage");
let camera: Camera = { x: 0, y: 0, z: 1 };
function setCamera(c: Camera) {
  camera = c;
  applyCamera(c);
}
function homeCamera(): Camera {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const z = Math.min(Math.max(Math.min(vw, vh) / 860, 0.5), 1);
  return cameraOver(0, 130, vw / 2, vh / 2, z);
}
function glideTo(c: Camera) {
  const world = $("world");
  world.classList.add("glide");
  setCamera(c);
  setTimeout(() => world.classList.remove("glide"), 400);
}

// ── boot ──
async function main() {
  // Draw the board immediately from local config — never block the first
  // paint on the Supabase library downloading from its CDN.
  initWorld($("world"));
  setCamera(homeCamera());
  $("local-banner").hidden = store.remote;

  const masthead = renderMasthead(cfg);
  const fmt = (d: Date) => d.toLocaleDateString("en-IN", { day: "numeric", month: "long" });
  setMastheadIntro(cfg.introLine.replace("{freezeDate}", fmt(freezeAt)));
  masthead.freezeLine.hidden = !frozen();
  masthead.freezeLine.textContent = cfg.freezeNotice;
  document.body.classList.add("ready"); // reveals the board (see styles)

  track("page_view", {
    mobile: matchMedia("(pointer: coarse)").matches,
    width: window.innerWidth,
    frozen: frozen(),
  });

  function peelSticker(note: Note) {
    const mine = mineOf(note.id);
    if (!mine || frozen()) return;
    store.remove(note.id, mine.token).catch(console.error);
    setMine(getMine().filter((m) => m.id !== note.id));
    notes.delete(note.id);
    removeNote(note.id);
    track("sticker_peeled");
  }

  function placeNote(n: Note, animate: boolean) {
    upsertNote(n, {
      mine: Boolean(mineOf(n.id)),
      animate,
      onRemove: n.kind === "sticker" ? () => peelSticker(n) : undefined,
    });
  }

  // Add any notes we don't already have on the board (skips duplicates,
  // so the fast fetch and the later reconcile never fight).
  function ingest(rows: Note[], animate: boolean) {
    for (const n of rows) {
      if (notes.has(n.id)) continue;
      if (n.pos_x == null || n.pos_y == null) {
        const p = spiralPosition(notes.size);
        n.pos_x = p.x;
        n.pos_y = p.y;
      }
      notes.set(n.id, n);
      placeNote(n, animate);
    }
  }

  // Paint the notes immediately from the lightweight REST fetch.
  const fastNotes = await fetchNotesFast();
  if (fastNotes) ingest(fastNotes, false);

  // ── collision rules: never cover the masthead or anyone's note ──
  function rectOf(n: Note): Rect {
    const { w, h } = noteSize(n.id);
    return { x: n.pos_x ?? 0, y: n.pos_y ?? 0, w, h };
  }
  function obstacles(excludeId?: string): Rect[] {
    const list: Rect[] = [mastheadRect()];
    // stickers are decorative: they never block anything
    for (const n of notes.values())
      if (n.id !== excludeId && n.kind !== "sticker") list.push(rectOf(n));
    return list;
  }

  /** First spiral spot that doesn't sit on anything. */
  function nextPosition() {
    const obs = obstacles();
    for (let i = 0; i < 600; i++) {
      const p = spiralPosition(i);
      if (!collides({ x: p.x, y: p.y, w: 240, h: 230 }, obs)) return p;
    }
    return spiralPosition(notes.size);
  }

  const mineCount = (kind: "note" | "sticker") =>
    getMine().filter((m) => (m.kind ?? "note") === kind).length;
  const atCap = () => mineCount("note") >= cfg.maxNotesPerPerson;
  const atStickerCap = () => mineCount("sticker") >= cfg.maxStickersPerPerson;

  function updateCapUI() {
    $("compose-open").hidden = frozen();
    $("compose-open").textContent = cfg.composeButton;
    $("compose-open").classList.toggle("capped", atCap());
    masthead.capLine.hidden = frozen() || !atCap();
    masthead.capLine.textContent = `you've pinned your ${cfg.maxNotesPerPerson} notes - tap one to edit, or remove one to write again`;
  }
  updateCapUI();

  // ── enlarge view ──
  function openView(note: Note) {
    const holder = $("view-note");
    holder.innerHTML = "";
    holder.appendChild(buildNoteBody(note as any));
    $("view-overlay").hidden = false;
  }
  $("view-overlay").onclick = () => ($("view-overlay").hidden = true);

  // ── compose ──
  let ghostWasNull = true;
  const compose = initCompose({
    cfg,
    isFrozen: frozen,
    atCap,
    nextPosition,
    onGhost(ghost) {
      setGhost(ghost);
      if (ghost && ghostWasNull) {
        // frame the ghost in the upper part of the viewport, above the sheet
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const z = Math.max(camera.z, 0.8);
        glideTo(cameraOver(ghost.pos_x, ghost.pos_y, vw / 2, vh * 0.3, z));
      }
      ghostWasNull = !ghost;
    },
    async submitNew(newNote, media) {
      const { note, token } = await createGated(newNote, media);
      notes.set(note.id, note);
      setMine([...getMine(), { id: note.id, token }]);
      placeNote(note, true);
      updateCapUI();
      track("note_created", {
        paper: note.paper,
        font: note.font,
        ink: note.ink,
        chars: note.message.length,
        has_photo: Boolean(note.photo_url),
        has_audio: Boolean(note.audio_url),
        signed: Boolean(note.name),
      });
      const email = $<HTMLInputElement>("note-email").value.trim();
      if (email) {
        store.saveEmail(email).catch(console.error);
        track("email_left");
      }
    },
    async submitEdit(note, token, fields, media) {
      const patch = await store.update(note.id, token, fields, media);
      Object.assign(note, patch);
      placeNote(note, false);
      track("note_edited");
    },
    track,
    async deleteNote(note, token) {
      await store.remove(note.id, token);
      setMine(getMine().filter((m) => m.id !== note.id));
      notes.delete(note.id);
      removeNote(note.id);
      updateCapUI();
      track("note_deleted");
    },
  });

  $("compose-open").onclick = () =>
    withRoomWord(() => {
      if (atCap()) {
        // show *why* instead of a silently missing button
        glideTo(homeCamera());
        masthead.capLine.classList.remove("pulse");
        requestAnimationFrame(() => masthead.capLine.classList.add("pulse"));
        return;
      }
      compose.open();
    });

  // create + translate a wrong-room-word rejection into plain words
  async function createGated(
    n: Parameters<Store["create"]>[0],
    media: Parameters<Store["create"]>[1]
  ) {
    try {
      return await store.create(n, media);
    } catch (e: any) {
      if (e?.code === "42501") {
        track("room_word_rejected");
        localStorage.removeItem("memorybox-word");
        // ask again, then retry the exact same action - nothing is lost
        askRoomWord(() => {
          if (n.kind === "sticker") void addSticker(n.message);
          else $("pin-btn").click();
        }, true);
        const err: any = new Error("room word rejected");
        err.friendly = "one moment - just checking the room word";
        throw err;
      }
      throw e;
    }
  }

  function flashCapLine(text: string) {
    masthead.capLine.textContent = text;
    masthead.capLine.hidden = false;
    masthead.capLine.classList.remove("pulse");
    requestAnimationFrame(() => masthead.capLine.classList.add("pulse"));
    setTimeout(updateCapUI, 2600);
  }

  // ── stickers: tiny decorative notes, draggable, may overlap ──
  const tray = $("sticker-tray");
  const grid = $("sticker-grid");
  STICKERS.forEach((s) => {
    const b = document.createElement("button");
    b.className = "sticker-choice";
    b.type = "button";
    const art = stickerArt(s);
    if (art) {
      const img = document.createElement("img");
      img.src = art;
      img.alt = "sticker";
      img.draggable = false;
      b.appendChild(img);
    } else b.textContent = s;
    b.onclick = () => addSticker(s);
    grid.appendChild(b);
  });

  $("sticker-open").hidden = frozen();
  $("sticker-open").onclick = () => (tray.hidden = !tray.hidden);

  // The note this person is editing, else their most recent own note.
  function ownAnchor(): { x: number; y: number } | null {
    const editingId = compose.editingId();
    const own = getMine()
      .filter((m) => (m.kind ?? "note") === "note")
      .map((m) => notes.get(m.id))
      .filter((n): n is Note => Boolean(n));
    const target =
      (editingId && notes.get(editingId)) || own[own.length - 1] || null;
    if (!target) return null;
    const { h } = noteSize(target.id);
    // just below the note, so it decorates rather than covers
    return { x: target.pos_x ?? 0, y: (target.pos_y ?? 0) + h / 2 + 40 };
  }

  async function addSticker(s: string) {
    tray.hidden = true;
    if (frozen()) return;
    if (!getRoomWord() && store.remote) {
      askRoomWord(() => void addSticker(s));
      return;
    }
    if (atStickerCap()) {
      glideTo(homeCamera());
      flashCapLine(
        `you've placed your ${cfg.maxStickersPerPerson} stickers - drag one somewhere new instead`
      );
      return;
    }
    // Anchor near the person's own most recent note (or the one being
    // edited) so the sticker lands where their attention is; fall back
    // to the middle of the current view if they haven't written yet.
    // A gentle scatter keeps repeats from stacking. Drag it anywhere after.
    const at = ownAnchor() ?? screenToWorld(camera, window.innerWidth / 2, window.innerHeight * 0.4);
    const jitter = 60;
    try {
      const { note, token } = await createGated(
        {
          kind: "sticker",
          message: s,
          name: null,
          paper: "cream",
          font: "caveat",
          ink: "graphite",
          rotation: +(Math.random() * 24 - 12).toFixed(1),
          pin: "brass",
          pos_x: Math.round(at.x + (Math.random() * 2 - 1) * jitter),
          pos_y: Math.round(at.y + (Math.random() * 2 - 1) * jitter),
        },
        NO_MEDIA
      );
      notes.set(note.id, note);
      setMine([...getMine(), { id: note.id, token, kind: "sticker" }]);
      placeNote(note, true);
      track("sticker_added", { sticker: s });
    } catch (e: any) {
      console.error("sticker didn't stick", e);
      glideTo(homeCamera());
      flashCapLine(e?.friendly ?? "the sticker didn't stick - try again");
    }
  }

  // local preview escape hatch: wipe this browser's test notes
  if (!store.remote) {
    const banner = $("local-banner");
    const reset = document.createElement("button");
    reset.className = "banner-reset";
    reset.textContent = "reset";
    reset.onclick = () => {
      if (!confirm("Clear this browser's preview notes and start fresh?")) return;
      localStorage.removeItem("memorybox-notes");
      localStorage.removeItem("memorybox-mine");
      localStorage.removeItem("memorybox-emails");
      location.reload();
    };
    banner.appendChild(reset);
  }

  // ── input machine ──
  attachInput(stage, {
    getCamera: () => camera,
    setCamera,
    canDrag: (id) => Boolean(mineOf(id)) && !frozen(),
    onTapNote(id) {
      const note = notes.get(id);
      if (!note) return;
      const mine = mineOf(id);
      if (note.kind === "sticker") {
        // tapping your own sticker reveals its × (tap again / elsewhere hides)
        const wasOpen = noteElOf(id)?.classList.contains("peelable");
        document.querySelectorAll(".note.peelable").forEach((n) => n.classList.remove("peelable"));
        if (mine && !frozen() && !wasOpen) noteElOf(id)?.classList.add("peelable");
        return;
      }
      // tapping anywhere else closes any open sticker ×
      document.querySelectorAll(".note.peelable").forEach((n) => n.classList.remove("peelable"));
      if (mine && !frozen() && !compose.isOpen()) compose.openEdit(note, mine.token);
      else openView(note);
    },
    onNoteMoved(id, wx, wy) {
      const note = notes.get(id);
      if (!note) return;
      note.pos_x = wx;
      note.pos_y = wy;
      moveNote(id, wx, wy, note.rotation);
      if (note.kind === "sticker") return; // stickers may overlap anything
      // live warning while hovering over someone else's note
      const { w, h } = noteSize(id);
      noteElOf(id)?.classList.toggle(
        "colliding",
        collides({ x: wx, y: wy, w, h }, obstacles(id))
      );
    },
    onNoteDropped(id, wx, wy) {
      const note = notes.get(id);
      const mine = mineOf(id);
      if (!note || !mine) return;
      // settle to the nearest spot that covers nobody else's note
      // (stickers are exempt - they decorate)
      const { w, h } = noteSize(id);
      const p =
        note.kind === "sticker"
          ? { x: Math.round(wx), y: Math.round(wy) }
          : resolve({ x: wx, y: wy, w, h }, obstacles(id));
      note.pos_x = p.x;
      note.pos_y = p.y;
      const el = noteElOf(id);
      el?.classList.remove("colliding");
      el?.classList.add("relaxing");
      moveNote(id, p.x, p.y, note.rotation);
      setTimeout(() => el?.classList.remove("relaxing"), 300);
      store
        .update(note.id, mine.token, { pos_x: p.x, pos_y: p.y }, NO_MEDIA)
        .catch(console.error);
      track("note_moved", { kind: note.kind ?? "note" });
    },
    getNotePos(id) {
      const n = notes.get(id);
      return { x: n?.pos_x ?? 0, y: n?.pos_y ?? 0 };
    },
  });

  // tapping empty board (or anything that isn't the open sticker) closes its ×
  stage.addEventListener("pointerdown", (e) => {
    const t = e.target as Element;
    if (!t.closest(".note.peelable")) {
      document.querySelectorAll(".note.peelable").forEach((n) => n.classList.remove("peelable"));
    }
  });

  // ── backend, in the background: live updates + a reconcile pass ──
  // The board is already on screen (fast REST fetch above); the Supabase
  // client now loads for realtime and posting without blocking paint.
  const presenceEl = $("presence");
  function startRealtime() {
    store.subscribe(
      (e) => {
        const id = e.note.id;
        if (e.type === "delete") {
          if (!notes.has(id)) return;
          notes.delete(id);
          removeNote(id);
          return;
        }
        const isMine = Boolean(mineOf(id));
        // own changes are already on screen - don't clobber a drag in progress
        if (isMine && notes.has(id)) return;
        const n = e.note;
        if (n.pos_x == null || n.pos_y == null) {
          const p = spiralPosition(notes.size);
          n.pos_x = p.x;
          n.pos_y = p.y;
        }
        const existed = notes.has(id);
        notes.set(id, n);
        placeNote(n, !existed);
      },
      (count) => {
        presenceEl.hidden = !store.remote || count < 2;
        presenceEl.textContent = `🤍 ${count} here right now`;
      }
    );
  }

  store
    .init()
    .then((serverFreeze) => {
      if (serverFreeze) {
        freezeAt = serverFreeze;
        masthead.freezeLine.hidden = !frozen();
      }
      startRealtime();
      // catch anything created between the fast fetch and going live
      store.list().then((all) => ingest(all, true)).catch(() => {});
    })
    .catch((e) => console.error("store init failed", e));

  // tap outside the word card to dismiss it
  $("word-overlay").addEventListener("click", (e) => {
    if (e.target === $("word-overlay")) $("word-overlay").hidden = true;
  });

  // (no resume dance needed: the word attaches without a reload)

  // ── recenter ──
  const recenter = $("recenter");
  recenter.hidden = false;
  recenter.onclick = () => glideTo(homeCamera());

  // block browser-level pinch/double-tap zoom on iOS Safari
  document.addEventListener("gesturestart", (e) => e.preventDefault());
  document.addEventListener("dblclick", (e) => e.preventDefault());
}

// Resolve which box to show, then construct the store and run.
async function boot() {
  const slug = parseSlug();
  if (slug) {
    const board = await fetchBoard(slug);
    if (!board) {
      document.body.classList.add("ready");
      $("world").innerHTML =
        '<div class="not-found">this memorybox link doesn\'t seem to exist 🤍</div>';
      return;
    }
    boardId = board.id;
    cfg = configFromBoard(board, BOARD_CONFIG.supabaseUrl, BOARD_CONFIG.supabaseAnonKey);
    freezeAt = new Date(cfg.freezeAt);
    if (board.frozen_early) freezeAt = new Date(0); // host froze it early
  }
  document.title = cfg.title;
  store =
    cfg.supabaseUrl && cfg.supabaseAnonKey
      ? new SupabaseStore(cfg.supabaseUrl, cfg.supabaseAnonKey, getRoomWord(), boardId)
      : new LocalStore();
  await main();
}

void boot();
